import { MarketOverviewWithNews, SingleNews } from '@api';
import { Drawer } from '@design/components/drawer/Drawer';
import { useDisableScroll } from '@helpers';
import { useMarketInsightsDrawer } from '@terminal/hooks/use-market-insights-drawer/useMarketInsightsDrawer';
import { ArticleDrawerContent } from '@terminal/widgets/news-widget/ArticleDrawerContent';
import clsx from 'clsx';
import { toZonedTime } from 'date-fns-tz';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getMarketInsightTitle } from '../market-insight-card/utils';
import styles from './MarketInsightDrawer.module.scss';

export const MarketInsightDrawer = ({
  activeInsightsGroup,
}: {
  activeInsightsGroup: MarketOverviewWithNews[];
}) => {
  const { t } = useTranslation([
    'whatsNew',
    'news',
    'chart',
    'analyze',
    'countries',
  ]);
  const [selectedNews, setSelectedNews] = useState<SingleNews | undefined>();
  const [activeArticleId, setActiveArticleId] = useState<string>('');

  useDisableScroll();
  const { closeDrawer } = useMarketInsightsDrawer();

  const onNewsPillClick = (news: SingleNews) => {
    setSelectedNews(news);
    setActiveArticleId(news.id);
  };

  const onCloseClick = () => {
    if (!!selectedNews) {
      setSelectedNews(undefined);
    } else {
      closeDrawer();
    }
  };

  const onBackgroundClick = () => {
    closeDrawer();
    setSelectedNews(undefined);
    setActiveArticleId('');
  };

  return (
    <Drawer
      className={clsx(styles.styledDrawer, {
        [styles.styledDrawerExpanded]: !!selectedNews,
      })}
      isOpen
      handleCloseClick={onCloseClick}
      customBackgroundClickHandler={onBackgroundClick}
    >
      <div className={styles.root}>
        <div className={styles.drawersWrapper}>
          <div className={styles.marketInsightsWrapper}>
            {activeInsightsGroup.map((insight, idx) => {
              const isLastItem = idx === activeInsightsGroup.length - 1;
              const isFirstItem = idx === 0;
              const zonedTime = toZonedTime(
                new Date(insight.date),
                'America/New_York'
              );
              return (
                <React.Fragment key={insight.id}>
                  <div className={styles.insightWrapper}>
                    {isFirstItem && (
                      <div className={styles.headerColumn}>
                        <div className={styles.headerTitle}>
                          {t('whatsNew:marketSummary')}
                        </div>
                        <div className={styles.headerSubtitle}>
                          {new Intl.DateTimeFormat(navigator.language, {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          }).format(zonedTime)}
                        </div>
                      </div>
                    )}
                    <div className={styles.summaryWrapper}>
                      <div className={styles.summaryTitle}>
                        {getMarketInsightTitle(insight.market_hours, t).title}
                      </div>
                      <div className={styles.summaryText}>
                        {insight.summary}
                      </div>
                      {!!insight.articles.length && (
                        <>
                          <div className={styles.readMoreLabel}>
                            {/* i18next-extract-mark-context-next-line ["benzinga", "mtn", "dowjones"] */}
                            {t('whatsNew:readMoreFrom', {
                              source: t('news:source', {
                                context: insight.articles[0].source,
                              }),
                            })}
                          </div>
                          <div className={styles.newsPillsWrapper}>
                            {insight.articles.map(news => (
                              <div
                                onClick={() => onNewsPillClick(news)}
                                key={news.id}
                                className={clsx(
                                  styles.newsPill,
                                  styles.newsPillDashboard
                                )}
                              >
                                <div className={styles.newsPillText}>
                                  {news.title}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {!isLastItem && (
                    <div>
                      <div className={styles.marketInsightSeparator} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {!!selectedNews && (
            <div className={styles.articleWrapper}>
              <div className={styles.verticalDivider} />
              <ArticleDrawerContent
                newsId={activeArticleId}
                data={selectedNews}
              />
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};
